import { useCallback, useSyncExternalStore } from 'react'
import { MEAL_SLOTS, type MealSlot } from '@shared/events'

/**
 * Preferencias de pantalla, por dispositivo.
 *
 * No son eventos del hogar: no cambian qué se come ni qué se compra, sólo qué
 * se enseña. Por eso viven en el navegador y no en la lista cerrada de tipos
 * que se sincronizan.
 */
const EXTRAS_KEY = 'recetario:mostrar-desayuno-merienda'

/** Las franjas que siempre se ven; desayuno y merienda se planifican más a ojo. */
export const FRANJAS_PRINCIPALES: readonly MealSlot[] = ['comida', 'cena']

const listeners = new Set<() => void>()

function leer(): boolean {
  try {
    return localStorage.getItem(EXTRAS_KEY) === '1'
  } catch {
    return false
  }
}

function suscribir(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (event: StorageEvent): void => {
    if (event.key === EXTRAS_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Ocultar una franja es sólo presentación: nunca borra lo que hay en ella, y
 * al volver a mostrarla aparece intacto.
 */
export function useMostrarExtras(): {
  mostrar: boolean
  alternar: () => void
  franjas: readonly MealSlot[]
} {
  const mostrar = useSyncExternalStore(suscribir, leer, () => false)

  const alternar = useCallback(() => {
    try {
      localStorage.setItem(EXTRAS_KEY, leer() ? '0' : '1')
    } catch {
      // Sin almacenamiento (modo privado estricto): la preferencia no persiste.
    }
    for (const listener of listeners) listener()
  }, [])

  return { mostrar, alternar, franjas: mostrar ? MEAL_SLOTS : FRANJAS_PRINCIPALES }
}
